import { Dialog, DialogTrigger, Modal, ModalOverlay } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import Button from 'src/components/Button/Button';
import InputText from 'src/components/InputText/InputText';
import RadioGroup from 'src/components/RadioGroup/RadioGroup';
import * as yup from 'yup';

import MultiSelectField from '@/components/SelectField/MultiSelectField';
import { UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { UserRequest } from '@/types/user';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: SubmitHandler<UserRequest>;
  offices: Office[];
};

// モーダルでの起動のため、Dialogコンポーネントを使用
const CreateUserForm = ({ isOpen, setIsOpen, onSubmit, offices }: Props) => {
  const schema: yup.ObjectSchema<UserRequest> = yup
    .object({
      last_name: yup.string().required('アカウント名（姓）は必須です'),
      first_name: yup.string().optional(),
      last_name_kana: yup.string().required('アカウント名（セイ）は必須です'),
      first_name_kana: yup.string().optional(),
      phone_number: yup.string().optional(),
      line_invite_link: yup.string().optional(), // 使わない
      email: yup
        .string()
        .email('メールアドレスの形式が正しくありません')
        .required('メールアドレスは必須です'),
      role: yup
        .string()
        .oneOf(['admin', 'staff', 'p_admin', 'p_user'])
        .required('アカウント権限は必須です'),
      office_ids: yup.array().of(yup.number().defined()).required('事業所は必須です'),
      children: yup.array().optional(),
    })
    .required();

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { isValid, errors },
  } = useForm<UserRequest>({
    resolver: yupResolver(schema),
  });

  const [loginUser] = useAtom(UserAtom);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <ModalOverlay>
        <Modal
          isDismissable
          className="fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-black/50"
        >
          <Dialog
            key="create-user-dialog"
            role="dialog"
            aria-label="アカウント登録"
            className="mt-24 h-full overflow-y-auto"
          >
            <div className="min-w-[900px] rounded-3xl bg-white p-10">
              <div className="flex flex-row justify-between">
                <h1 className="mb-5 text-2xl font-700">アカウント登録</h1>
              </div>
              <form
                key="create-user-form"
                onSubmit={handleSubmit(onSubmit)}
                className="w-full p-10"
              >
                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="text"
                    required
                    placeholder="山田"
                    label="アカウント名（姓）"
                    errors={errors.last_name}
                    {...register('last_name', { required: true })}
                  />
                  <InputText
                    type="text"
                    label="アカウント名（名）"
                    placeholder="太郎"
                    errors={errors.first_name}
                    {...register('first_name')}
                  />
                </div>

                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="text"
                    required
                    placeholder="ヤマダ"
                    label="アカウント名（セイ）"
                    errors={errors.last_name_kana}
                    {...register('last_name_kana', { required: true })}
                  />
                  <InputText
                    type="text"
                    label="アカウント名（メイ）"
                    placeholder="タロウ"
                    errors={errors.first_name_kana}
                    {...register('first_name_kana')}
                  />
                </div>

                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="tel"
                    label="連絡先TEL"
                    placeholder="08012345678"
                    errors={errors.phone_number}
                    {...register('phone_number')}
                  />
                  <InputText
                    type="email"
                    required
                    placeholder="example@pla.co.jp"
                    label="メールアドレス"
                    errors={errors.email}
                    {...register('email', { required: true })}
                  />
                </div>

                <RadioGroup
                  title="アカウント権限"
                  required
                  supportText="権限を選択してください"
                  errors={errors.role}
                  options={[
                    {
                      id: 'admin',
                      label: '管理者（admin）',
                      value: 'admin',
                    },
                    {
                      id: 'staff',
                      label: '一般（staff）',
                      value: 'staff',
                    },
                    {
                      id: 'p_admin',
                      label: '保護者（p_admin）',
                      value: 'p_admin',
                    },
                    {
                      id: 'p_user',
                      label: '保護者（p_user）',
                      value: 'p_user',
                    },
                  ]}
                  {...register('role', { required: true })}
                />

                <div className="flex justify-between space-x-2 p-4">
                  <MultiSelectField
                    name="office_ids"
                    label="事業所を選択"
                    placeholder="事業所を選択"
                    options={offices
                      .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                      .map((office) => ({
                        label: office.name,
                        value: office.id,
                      }))}
                    control={control}
                    required
                  />
                </div>

                {watch('role') === 'p_admin' && (
                  <>
                    <div className="flex justify-between space-x-2 p-4">
                      <InputText
                        type="text"
                        label="児童名（姓）"
                        placeholder="山田"
                        required={
                          watch('children.0.last_name') !== '' ||
                          watch('children.0.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[0]?.last_name}
                        {...register('children.0.last_name')}
                      />
                      <InputText
                        type="text"
                        label="児童名（名）"
                        placeholder="孝之"
                        required={
                          watch('children.0.last_name') !== '' ||
                          watch('children.0.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[0]?.first_name}
                        {...register('children.0.first_name')}
                      />
                      <MultiSelectField
                        name="children.0.office_ids"
                        label="事業所選択"
                        placeholder="事業所を選択"
                        options={offices
                          .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        control={control}
                        required
                      />
                    </div>
                    <div className="flex justify-between space-x-2 p-4">
                      <InputText
                        type="text"
                        label="児童名（姓）"
                        placeholder="山田"
                        errors={errors.children?.[1]?.last_name}
                        {...register('children.1.last_name')}
                      />
                      <InputText
                        type="text"
                        label="児童名（名）"
                        placeholder="孝之"
                        errors={errors.children?.[1]?.first_name}
                        {...register('children.1.first_name')}
                      />
                      <MultiSelectField
                        name="children.1.office_ids"
                        label="事業所を選択"
                        placeholder="事業所を選択"
                        options={offices
                          .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        control={control}
                        required
                      />
                    </div>
                  </>
                )}

                <div className="mt-10 flex items-center justify-center space-x-2">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setIsOpen(false)}
                  >
                    戻る
                  </Button>
                  <Button
                    variant="solid-fill"
                    size="lg"
                    type="submit"
                    disabled={!isValid}
                  >
                    保存
                  </Button>
                </div>
              </form>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

export default CreateUserForm;
